import { useMemo, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import PageBanner from "@/components/feature/PageBanner";
import { useApi } from "@/hooks/useApi";
import type { MemberDetail as MemberDetailType } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import EmptyState from "@/components/base/EmptyState";
import Avatar from "@/components/base/Avatar";
import Lightbox from "@/components/base/Lightbox";
import ContactModal from "@/components/feature/ContactModal";
import { careerPillClass } from "@/components/feature/MemberCard";
import { contrastText, validColor } from "@/lib/color";
import MiniMap from "../components/MiniMap";
import { mediaUrl } from "@/lib/media";

const FALLBACK_COLOR = "#17408B";

// 라벨-값 2열 정의형 한 줄 (모바일에서는 라벨 위 / 값 아래로 쌓임)
function DlRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-[80px_1fr] sm:gap-3">
      <dt className="text-foreground-500">{label}</dt>
      <dd className="min-w-0 break-words font-medium text-foreground-800">{children}</dd>
    </div>
  );
}

// "2019-03-05" → "2019. 03. 05."
function formatDate(value?: string | null): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "";
  const m = raw.match(/^(\d{4})[-./](\d{1,2})[-./](\d{1,2})/);
  if (!m) return raw;
  return `${m[1]}. ${m[2].padStart(2, "0")}. ${m[3].padStart(2, "0")}.`;
}

function onlyDigits(value: string): string {
  return value.replace(/[^0-9+]/g, "");
}

function homepageHref(url: string): string {
  if (!url) return "";
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}

function homepageLabel(url: string): string {
  return url
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .replace(/\/+$/, "");
}

export default function MemberDetail() {
  const { no } = useParams<{ no: string }>();
  const { data, loading, error, reload } = useApi<MemberDetailType | MemberDetailType[]>(
    no ? `member_view.php?no=${no}` : null
  );
  const [contactOpen, setContactOpen] = useState(false);
  const [lightbox, setLightbox] = useState<number | null>(null);
  const [copied, setCopied] = useState(false);

  const member: MemberDetailType | null = Array.isArray(data) ? data[0] ?? null : data;

  const cardImages = useMemo(() => {
    if (!member) return [];
    return [member.card_img1, member.card_img2].filter(Boolean) as string[];
  }, [member]);

  const cate = String(member?.cate_name || member?.cate || member?.category || "").trim();
  const cateColor = validColor(String(member?.cate_color ?? "")) || validColor(String(member?.color ?? "")) || FALLBACK_COLOR;
  const color = validColor(String(member?.color ?? "")) || FALLBACK_COLOR;
  const lat = Number(member?.lat);
  const lon = Number(member?.lon);

  // 직장 노출 여부 (0이면 직장정보 비공개)
  const isBizShow = member?.is_biz_show === undefined ? 1 : Number(member.is_biz_show);
  const bizHidden = isBizShow === 0;

  // 주소: road_addr 우선 → biz_addr → address/addr
  const roadAddr = String(member?.road_addr ?? "").trim();
  const bizAddr = String(member?.biz_addr ?? "").trim();
  const rawAddress =
    roadAddr || bizAddr || String(member?.address ?? member?.addr ?? "").trim();
  const addressDisplay = bizHidden ? "비공개" : rawAddress || "미등록";
  const addressMuted = bizHidden || !rawAddress;

  // 휴대폰
  const hp = String(member?.hp ?? "").trim();
  const hpHidden = Number(member?.hp_hidden ?? 0) === 1;
  const phoneMuted = hpHidden || !hp;

  // 사업장 값
  const bizName = String(member?.biz_name || member?.company || "").trim();
  const bizPosition = String(member?.biz_position || member?.position || "").trim();
  const bizType = String(member?.biz_type ?? "").trim();
  const bizTel = String(member?.biz_tel ?? "").trim();
  const bizIntro = String(member?.biz_intro || member?.intro || "").trim();
  const bizUrl = String(member?.biz_url || member?.homepage || "").trim();

  // 회원 정보
  const joinDate = formatDate(member?.join_date);
  const birth = formatDate(member?.birth);
  const isLunar = Number(member?.is_lunar ?? 0) === 1;

  const handleCopyAddress = async () => {
    if (!rawAddress || bizHidden) return;
    try {
      await navigator.clipboard.writeText(rawAddress);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* 클립보드 사용 불가 시 무시 */
    }
  };

  // 사업장 정보 카드: 직장명이 있거나, 직장정보를 비공개(0)로 설정한 회원이면 주소 줄을 위해 노출
  const showBizCard = Boolean(bizName) || isBizShow === 0;
  const showMemberInfo = Boolean(joinDate || birth || member?.hp !== undefined || member);

  // 지도 카드: 서버가 has_map 을 1 로 줄 때만 렌더링 (프런트에서 임의 판단 금지)
  const hasMap = Number(member?.has_map ?? 0) === 1;

  return (
    <>
      <PageBanner
        title="회원 상세"
        breadcrumb={[{ label: "회원", to: "/members" }, { label: "상세" }]}
      />

      <section className="mx-auto max-w-4xl px-4 md:px-6 py-10 md:py-12">
        {loading ? (
          <div className="space-y-6">
            <div className="flex items-center gap-5">
              <Skeleton className="h-24 w-24 rounded-full" />
              <div className="space-y-3">
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
        ) : error ? (
          <EmptyState
            icon="ri-error-warning-line"
            title="회원 정보를 불러오지 못했습니다"
            description={error}
            action={
              <button
                type="button"
                onClick={reload}
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                다시 시도
              </button>
            }
          />
        ) : !member ? (
          <EmptyState
            icon="ri-user-line"
            title="존재하지 않는 회원입니다"
            action={
              <Link
                to="/members"
                className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
              >
                목록으로
              </Link>
            }
          />
        ) : (
          <div className="space-y-6">
            {/* 프로필 */}
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 rounded-xl border border-background-200 bg-background-50 p-6">
              <Avatar name={member.name} photo={member.photo} color={color} size={96} />
              <div className="text-center sm:text-left">
                <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  {member.name}
                  {member.aho && (
                    <span className="ml-2 text-base font-medium text-foreground-500">
                      ({member.aho})
                    </span>
                  )}
                </h1>
                <div className="mt-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  {cate && (
                    <span
                      className="inline-block rounded-full px-3 py-1 text-xs font-semibold"
                      style={{ backgroundColor: cateColor, color: contrastText(cateColor) }}
                    >
                      {cate}
                    </span>
                  )}
                  {member.club_role && (
                    <span className="inline-block rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
                      {member.club_role}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* 회원 정보 */}
            {showMemberInfo && (
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  회원 정보
                </h2>
                <dl className="mt-4 space-y-3 text-sm">
                  {joinDate && <DlRow label="입회일">{joinDate}</DlRow>}
                  {birth && (
                    <DlRow label="생일">
                      {birth}
                      {isLunar && <span className="ml-1 text-foreground-500">(음)</span>}
                    </DlRow>
                  )}
                  <DlRow label="휴대폰">
                    {hpHidden ? (
                      <span className="text-foreground-400">비공개</span>
                    ) : hp ? (
                      <a
                        href={`tel:${onlyDigits(hp)}`}
                        className="text-primary-700 hover:underline"
                      >
                        {hp}
                      </a>
                    ) : (
                      <span className="text-foreground-400">미등록</span>
                    )}
                  </DlRow>
                </dl>
              </div>
            )}

            {/* 로타리 경력 */}
            {member.career && member.career.length > 0 && (
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  로타리 경력
                </h2>
                <div className="mt-4 flex flex-col items-start gap-1.5">
                  {member.career.map((c, i) => (
                    <span
                      key={i}
                      className={`inline-block max-w-full whitespace-normal break-keep rounded-md px-2.5 py-1 text-[12.5px] leading-[1.35] ${careerPillClass(c)}`}
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* 사업장 정보 */}
            {showBizCard && (
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  사업장 정보
                </h2>
                <dl className="mt-4 space-y-3 text-sm">
                  {bizName && <DlRow label="직장명">{bizName}</DlRow>}

                  {!bizHidden && bizPosition && <DlRow label="직책">{bizPosition}</DlRow>}

                  {!bizHidden && (cate || bizType) && (
                    <DlRow label="업종">
                      <span className="flex flex-wrap items-center gap-2">
                        {cate && (
                          <span
                            className="whitespace-nowrap rounded-full px-2.5 py-[1px] text-[12px] font-semibold"
                            style={{
                              backgroundColor: cateColor,
                              color: contrastText(cateColor),
                            }}
                          >
                            {cate}
                          </span>
                        )}
                        {bizType && <span className="text-foreground-800">{bizType}</span>}
                      </span>
                    </DlRow>
                  )}

                  {!bizHidden && bizTel && (
                    <DlRow label="전화">
                      <a
                        href={`tel:${onlyDigits(bizTel)}`}
                        className="text-primary-700 hover:underline"
                      >
                        {bizTel}
                      </a>
                    </DlRow>
                  )}

                  {/* 주소는 항상 표시 */}
                  <DlRow label="주소">
                    <span className="flex items-start gap-2">
                      <span className={addressMuted ? "text-foreground-400" : ""}>
                        {addressDisplay}
                      </span>
                      {!addressMuted && (
                        <button
                          type="button"
                          onClick={handleCopyAddress}
                          aria-label="주소 복사"
                          title="주소 복사"
                          className="mt-[1px] inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-800"
                        >
                          <i
                            className={copied ? "ri-check-line text-[15px]" : "ri-file-copy-line text-[15px]"}
                            aria-hidden="true"
                          ></i>
                        </button>
                      )}
                    </span>
                  </DlRow>

                  {!bizHidden && bizUrl && (
                    <DlRow label="홈페이지">
                      <a
                        href={homepageHref(bizUrl)}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="inline-flex items-center gap-1 text-primary-700 hover:underline"
                      >
                        {homepageLabel(bizUrl)}
                        <i className="ri-external-link-line text-[13px]" aria-hidden="true"></i>
                      </a>
                    </DlRow>
                  )}
                </dl>

                {!bizHidden && bizIntro && (
                  <p className="mt-5 whitespace-pre-wrap rounded-lg bg-background-100 p-4 text-[14px] leading-relaxed text-foreground-700">
                    {bizIntro}
                  </p>
                )}
              </div>
            )}

            {/* 지도 + 액션 — has_map === 1 일 때만 카드 전체 렌더링 */}
            {hasMap && Number.isFinite(lat) && Number.isFinite(lon) && (
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                  위치
                </h2>
                {rawAddress && !bizHidden && (
                  <p className="mb-3 flex items-start gap-1.5 text-sm text-foreground-700">
                    <i className="ri-map-pin-line mt-0.5 shrink-0 text-foreground-400" aria-hidden="true"></i>
                    <span>{rawAddress}</span>
                  </p>
                )}
                <MiniMap lat={lat} lon={lon} name={member.biz_name || member.name} className="h-64 w-full" />
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {!hpHidden && hp && (
                    <a
                      href={`tel:${onlyDigits(hp)}`}
                      className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
                    >
                      <i className="ri-phone-line" aria-hidden="true"></i>
                      전화걸기
                    </a>
                  )}
                  <a
                    href={`https://map.kakao.com/link/to/${encodeURIComponent(member.biz_name || member.name)},${lat},${lon}`}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100 transition-colors"
                  >
                    <i className="ri-navigation-line" aria-hidden="true"></i>
                    길찾기
                  </a>
                  <button
                    type="button"
                    onClick={() => setContactOpen(true)}
                    className="inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors"
                  >
                    <i className="ri-mail-send-line" aria-hidden="true"></i>
                    문의하기
                  </button>
                </div>
              </div>
            )}

            {/* 명함 이미지 */}
            {cardImages.length > 0 && (
              <div className="rounded-xl border border-background-200 bg-background-50 p-6">
                <h2 className="font-heading text-lg font-bold text-foreground-950 mb-4">
                  명함
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {cardImages.map((img, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setLightbox(i)}
                      className="overflow-hidden rounded-lg border border-background-200 bg-background-100"
                      aria-label={`명함 ${i + 1} 크게 보기`}
                    >
                      <span className="relative block aspect-[9/5] w-full">
                        <img
                          src={mediaUrl(img)}
                          alt={`${member.name} 명함 ${i + 1}`}
                          className="h-full w-full object-contain"
                        />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-center">
              <Link
                to="/members"
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-background-100"
              >
                <i className="ri-list-check" aria-hidden="true"></i>
                목록으로
              </Link>
            </div>
          </div>
        )}
      </section>

      <ContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        memNo={member?.no}
        title={member?.biz_name ? `${member.biz_name} 문의하기` : "문의하기"}
      />

      {lightbox != null && (
        <Lightbox
          images={cardImages}
          index={lightbox}
          onIndexChange={setLightbox}
          onClose={() => setLightbox(null)}
          alt={`${member?.name ?? ""} 명함`}
        />
      )}
    </>
  );
}